import { useState } from 'react';
import useApiClient from '@src/hooks/useApiClient';
import type { IdentificationAuthMode } from '@src/domains/customers';
import { decideAuthRoute } from '@src/features/customers/auth/identification/core/decideAuthRoute';

type UseCheckEmailAvailabilityProps = {
  throwError?: boolean;
};

/**
 * Checks the email (checkEmail) and resolves the destination (login or signup) via
 * `decideAuthRoute`. Returns `null` and exposes `error` when the check fails, so the
 * caller decides how to surface it.
 */
export function useCheckEmailAvailability(props?: UseCheckEmailAvailabilityProps) {
  const { throwError = false } = props || {};
  const { bearerAuth, buyers } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const identify = async (email: string): Promise<IdentificationAuthMode | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await buyers.checkEmail(email, { ...bearerAuth });
      return decideAuthRoute(data.available);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('checkEmail failed:', message);
      setError(message);

      if (throwError) {
        throw err;
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  return { identify, loading, error };
}
